/** biome-ignore-all lint/suspicious/noExplicitAny: any type*/
import React from 'react';

import debounce from 'lodash.debounce';

import { useUnmount } from './use-unmount';

type DebounceOptions = {
  leading?: boolean;
  trailing?: boolean;
  maxWait?: number;
};

type ControlFunctions = {
  cancel: () => void;
  flush: () => void;
  isPending: () => boolean;
};

export type DebouncedState<T extends (...args: any) => ReturnType<T>> = ((
  ...args: Parameters<T>
) => ReturnType<T> | undefined) &
  ControlFunctions;

export const useDebouncedCallback = <T extends (...args: any) => ReturnType<T>>(
  func: T,
  delay = 500,
  options?: DebounceOptions
): DebouncedState<T> => {
  // The latest callback, so the debounce itself does not have to be rebuilt on every render.
  // Rebuilding it lost pending calls: cancel() reached a new instance, not the pending one
  const funcRef = React.useRef(func);
  funcRef.current = func;

  const leading = options?.leading;
  const trailing = options?.trailing;
  const maxWait = options?.maxWait;

  const debounced = React.useMemo(() => {
    let pending = false;
    const debouncedFuncInstance = debounce(
      (...args: Parameters<T>) => {
        // The call ran, so nothing is pending any more
        pending = false;
        return funcRef.current(...args);
      },
      delay,
      {
        ...(leading !== undefined && { leading }),
        ...(trailing !== undefined && { trailing }),
        ...(maxWait !== undefined && { maxWait }),
      }
    );

    const wrappedFunc = ((...args: Parameters<T>) => {
      // Without a trailing call a call inside the wait is dropped, so nothing becomes pending
      if (trailing !== false) pending = true;
      return debouncedFuncInstance(...args);
    }) as DebouncedState<T>;

    wrappedFunc.cancel = () => {
      pending = false;
      debouncedFuncInstance.cancel();
    };

    wrappedFunc.isPending = () => pending;

    wrappedFunc.flush = () => {
      pending = false;
      return debouncedFuncInstance.flush();
    };

    return wrappedFunc;
  }, [delay, leading, trailing, maxWait]);

  useUnmount(() => {
    debounced.cancel();
  });

  return debounced;
};
